import startCase from 'lodash.startcase'
import Link from 'next/link'
import { useRouter } from 'next/router'
import numeral from 'numeral'
import { ComponentType, useMemo } from 'react'

import { useUser } from 'src/providers/UserProvider'

import {
  BASE_CLASSES,
  VARIANT_CLASSES
} from 'src/components/atoms/ButtonRebrand/styles'
import { Calendar } from 'src/components/atoms/Icons/Calendar'
import { Gifted } from 'src/components/atoms/Icons/Gifted'
import { Gifter } from 'src/components/atoms/Icons/Gifter'
import { IconProps } from 'src/components/atoms/Icons/types'
import { Image } from 'src/components/atoms/Image'
import { InternalLink } from 'src/components/atoms/Link'
import { ShareActions } from 'src/components/atoms/ShareActions'
import { TypographyRebrand } from 'src/components/atoms/TypographyRebrand'
import { RegistryEventFeedRebrand } from 'src/components/molecules/RegistryEventFeedRebrand'

import { editRegistryPath, registryGiftPath } from 'src/config/paths'
import { env } from 'src/env/client.mjs'
import { NotFound } from 'src/pages/404/404'
import { isHidden } from 'src/utils/account'
import { difference, formatDate } from 'src/utils/date'
import { trpc } from 'src/utils/trpc'

type StatProps = {
  value: string
  label: string
  icon: ComponentType<IconProps>
  text?: string
}

const Stat = ({ value, label, icon: Icon, text }: StatProps) => (
  <div className='flex items-center gap-4'>
    {/* Icons kept as-is (old brand artwork); typography rebranded. */}
    {/* eslint-disable-next-line @next/next/no-img-element */}
    <Icon className='w-14 h-14 text-forestDeep' />
    <div className='flex flex-col gap-px'>
      <span className='font-display-rebrand font-extrabold text-[32px] leading-[1.2] text-forestDeep'>
        {value}
      </span>
      <span className='font-body-rebrand text-base leading-[1.6] text-forestDeep/60'>
        {label}
      </span>
      {text ? (
        <span className='font-body-rebrand text-sm text-forestDeep/60 mt-1'>
          {text}
        </span>
      ) : null}
    </div>
  </div>
)

const RegistryDetailSectionRebrand = () => {
  const router = useRouter()
  const { user } = useUser()
  const { registryUri } = router.query as { registryUri: string }

  const { data, isLoading } = trpc.useQuery([
    'registries.getByUri',
    { uri: registryUri }
  ])

  const registry = data?.registry

  const daysToGo = useMemo(
    () =>
      registry?.ClosesOn
        ? difference({ date1: registry.ClosesOn, isUtc: true })
        : null,
    [registry?.ClosesOn]
  )

  const endsOn = useMemo(
    () =>
      registry?.ClosesOn
        ? `Ends on ${formatDate({
            date: registry.ClosesOn,
            format: 'Do of MMMM YYYY',
            isUtc: true
          })}.`
        : null,
    [registry?.ClosesOn]
  )

  if (isLoading) return null
  if (!registry) return <NotFound />

  // The page redirects campaigns to the crowdfund route; render nothing here.
  if (registry.IsCampaign) return null

  const isOwner = registry.Account?.Id === user?.Account?.Id
  const funderName = registry.Account?.Name
  const addGiftHref = registryGiftPath({
    routeParams: { registryUri: registry.Uri! }
  })

  const createdOn = registry.CreatedOn
    ? formatDate({
        date: registry.CreatedOn,
        format: 'D MMMM YYYY',
        isUtc: true
      })
    : null

  const renderAddGiftButton = () => (
    <Link
      href={addGiftHref}
      className={`${BASE_CLASSES} ${VARIANT_CLASSES.lime}`}
    >
      Add your gift
    </Link>
  )

  return (
    <div className='w-full flex flex-col py-12 md:py-20 gap-12 md:gap-20'>
      <div className='container mx-auto px-4 lg:px-20 flex flex-col gap-6'>
        <TypographyRebrand variant='h2' color='forestDeep'>
          {registry.Name ?? 'Tree registry'}
        </TypographyRebrand>
        <div className='flex flex-wrap gap-4'>
          {renderAddGiftButton()}
          {isOwner && (
            <Link
              href={editRegistryPath({
                routeParams: { registryUri: registry.Uri! }
              })}
              className={`${BASE_CLASSES} ${VARIANT_CLASSES.outline} border-cream! text-cream! hover:bg-cream/10!`}
            >
              Edit
            </Link>
          )}
        </div>
      </div>

      <div className='container mx-auto px-4 lg:px-20 flex flex-col gap-12'>
        <div className='lg:flex lg:gap-12'>
          <div className='shrink-0'>
            <Image
              key={registry.Hero}
              alt={`${registry.Name} profile picture`}
              src={`${env.NEXT_PUBLIC_ASSETS_DOMAIN}/${registry.Hero}`}
              fallbackSrc='/img/default.png'
              height={600}
              width={600}
            />
          </div>
          <div className='flex flex-col gap-5'>
            {registry.Trees !== null && (
              <Stat
                value={numeral(registry.Trees || 0).format('0,0')}
                label='Trees gifted'
                icon={Gifted}
              />
            )}
            {registry.Gifts !== null && (
              <Stat
                value={numeral(registry.Gifts || 0).format('0,0')}
                label='Gifters'
                icon={Gifter}
              />
            )}
            {daysToGo && endsOn ? (
              <Stat
                value={numeral(daysToGo).format('0,0')}
                label='Days left to give'
                icon={Calendar}
                text={endsOn}
              />
            ) : null}
            <div className='flex flex-col gap-2 mt-2'>
              <TypographyRebrand variant='h5' color='forestDeep'>
                Tree registry created by
              </TypographyRebrand>
              {!isHidden(registry.Account) ? (
                <InternalLink
                  className='font-body-rebrand text-lg text-forestDeep hover:text-forestDeepHover'
                  href={`/profiles/${registry.Account?.Uri}`}
                >
                  {funderName}
                </InternalLink>
              ) : (
                <span className='font-body-rebrand text-lg text-forestDeep'>
                  {funderName}
                </span>
              )}
              {createdOn && (
                <span className='font-body-rebrand md:text-lg text-forestDeep'>
                  {`Created on ${createdOn}`}
                </span>
              )}
              {registry.Account?.Type && (
                <span className='font-body-rebrand text-sm uppercase text-forestDeep/60'>
                  {startCase(registry.Account.Type)}
                </span>
              )}
              <div className='mt-2'>{renderAddGiftButton()}</div>
            </div>
            <div className='flex flex-col gap-3 mt-2'>
              <TypographyRebrand variant='h5' color='forestDeep'>
                Share this registry
              </TypographyRebrand>
              <ShareActions />
            </div>
          </div>
        </div>

        <div className='flex flex-col gap-4'>
          <TypographyRebrand variant='h4' color='forestDeep'>
            About this tree registry
          </TypographyRebrand>
          <p className='font-body-rebrand text-base md:text-lg leading-[1.6] text-forestDeep whitespace-pre-wrap m-0'>
            {registry.Description}
          </p>
        </div>

        {registry.Id && <RegistryEventFeedRebrand registryId={registry.Id} />}
      </div>
    </div>
  )
}

export default RegistryDetailSectionRebrand
